import { useLingui } from "@lingui/react/macro";
import { FormInput } from "@offstreetparking/design-system/forms";
import { Heading, Text } from "@offstreetparking/design-system/typography";
import { offstreetClient } from "../../../../api/client";
import { Form } from "../../../forms/Form";
import { useZod } from "../../../providers/TranslationsProvider";
import { usePermitFormActions } from "../../PermitContextProvider";
import type { CodeFormData } from "../../PermitContextProvider/types";
type EnterCodeFormProps = {
	formId: string;
	permitTypeId: number;
	isEvent: boolean;
	initialValue: Partial<CodeFormData>;
	onSubmit: (data: CodeFormData) => void;
	codeLabel?: string;
	codeDescription?: string;
	permitIntervals?: {
		startDateTimeISO: string;
		endDateTimeISO?: string | null;
	}[];
};
export const EnterCodeForm = ({
	formId,
	initialValue,
	permitTypeId,
	isEvent,
	onSubmit,
	codeLabel,
	codeDescription,
	permitIntervals,
}: EnterCodeFormProps) => {
	const { t } = useLingui();
	const z = useZod();
	const { mutate: validateCode } = offstreetClient.registrationCode.validateCode.useMutation();
	const { setError, setIsProcessingPermit } = usePermitFormActions();
	return (
		<div className="h-full">
			<Form
				id={formId}
				onSubmit={async (data) => {
					setError(undefined);
					setIsProcessingPermit(true);
					validateCode(
						{
							code: data.code,
							permitTypeId,
							intervals: permitIntervals?.map((i) => ({
								startDateTimeISO: i.startDateTimeISO,
								endDateTimeISO: i.endDateTimeISO ?? undefined,
							})),
						},
						{
							onError: (error) => {
								setError(error);
								setIsProcessingPermit(false);
							},
							onSuccess: () => onSubmit(data),
						},
					);
				}}
				initialValues={initialValue}
				schema={z.object({
					code: z.string().min(1),
				})}
			>
				<Heading>{t`Enter your code`}</Heading>
				<FormInput
					name="code"
					label={
						codeLabel ? (
							<Text size="sm" className="font-bold">
								{codeLabel}
							</Text>
						) : (
							t`Code`
						)
					}
					description={codeDescription}
					placeholder={t`Enter code`}
					isRequired
					type="password"
					toggleAriaLabel={codeLabel ?? t`Code`}
				/>
			</Form>
			<div className="mt-8">
				<Text size="md" className="font-bold text-primary">
					{t`Can't find your code?`}
				</Text>
				<Text size="sm" className="mt-2">
					{isEvent
						? t`Parking registration codes are usually included in the email you received to book parking. If you can't find your code, please contact your event host or check the Help and Support section on this page for a contact email.`
						: t`Parking registration codes are usually provided by the property manager, business, or institution you're visiting. If applicable, they may also be posted on signage near the QR code. You can also check the Help and Support section on this page for a contact email.`}
				</Text>
			</div>
		</div>
	);
};
